/**
 * InputGroup molecule — input with addons (icon, button, text).
 *
 * Layouts:
 * - inline: addons overlay input (inline-start, inline-end)
 * - block: addons above/below (block-start, block-end), card-style container
 *
 * Size:
 * - Set `size` on InputGroup (sm | md | lg). Input and Button inherit it.
 * - InputGroupButton uses inset heights matched to Input of the same size
 *   (Button py-* alone is NOT compatible with Input h-* tokens).
 *
 * Error handling:
 * - Inline: Input renders error below itself
 * - Block: InputGroup renders error below the card; Input receives aria-invalid only
 *
 * @see https://ui.shadcn.com/docs/components/radix/input-group
 */

import React from "react";
import { Input } from "../../atoms/Input";
import type { InputSize } from "../../atoms/Input";
import { Typography } from "../../atoms/Typography";
import { Button } from "../../atoms/Button";
import { Textarea } from "../../textarea";
import {
  getInputGroupAddonClasses,
  getInputGroupInputClasses,
  getInputGroupWrapperClasses,
  getInputGroupInputWrapperClasses,
  getInputGroupInputBlockClasses,
  getInputGroupButtonClasses,
} from "./InputGroup.styles";
import { discoverInlineAddons, injectPaddingIntoInputs } from "./InputGroup.utils";
import { cn } from "@/utils/cn";
import type {
  InputGroupProps,
  InputGroupAddonProps,
  InputGroupTextProps,
  InputGroupInputProps,
  InputGroupContextValue,
  InputGroupButtonProps,
  InputGroupTextareaProps,
} from "./InputGroup.types";

export type { InputGroupAddonAlign } from "./InputGroup.types";

const InputGroupContext = React.createContext<InputGroupContextValue>({
  orientation: "inline",
  size: "md",
  hasInlineStartAddon: false,
  hasInlineEndAddon: false,
  blockError: undefined,
});

const InputGroup: React.FC<InputGroupProps> = ({
  children,
  orientation = "inline",
  size = "md",
  className,
}) => {
  const flags = discoverInlineAddons(children, InputGroupAddon);
  const { enhancedChildren, blockError } = injectPaddingIntoInputs(
    children,
    flags,
    InputGroupInput,
    orientation
  );

  const showErrorOutside = orientation === "block" && blockError;

  return (
    <InputGroupContext.Provider
      value={{
        orientation,
        size,
        hasInlineStartAddon: flags.hasInlineStartAddon,
        hasInlineEndAddon: flags.hasInlineEndAddon,
        blockError,
      }}
    >
      <div className={cn("flex w-full min-w-0 flex-col", className)}>
        <div className={getInputGroupWrapperClasses(orientation)}>
          {enhancedChildren}
        </div>
        {showErrorOutside && (
          <Typography
            as="p"
            variant="body-xs"
            weight="medium"
            color="error"
            className="mt-1"
          >
            {blockError}
          </Typography>
        )}
      </div>
    </InputGroupContext.Provider>
  );
};

const InputGroupInput = React.forwardRef<HTMLInputElement, InputGroupInputProps>(
  (
    {
      className,
      endAddonPadding = "icon",
      startAddonPadding = "none",
      size: sizeProp,
      ...props
    },
    ref
  ) => (
    <InputGroupContext.Consumer>
      {({ orientation, blockError, size: groupSize }) => {
        const isBlock = orientation === "block";
        const errorHandledByGroup = isBlock && blockError;
        const inputProps = errorHandledByGroup ? { ...props, error: undefined } : props;
        const size = sizeProp ?? groupSize;
        return (
          <div className={getInputGroupInputWrapperClasses(isBlock)}>
            <Input
              ref={ref}
              size={size}
              className={cn(
                getInputGroupInputClasses(startAddonPadding, endAddonPadding, className),
                getInputGroupInputBlockClasses(isBlock)
              )}
              {...inputProps}
            />
          </div>
        );
      }}
    </InputGroupContext.Consumer>
  )
);
InputGroupInput.displayName = "InputGroupInput";

const InputGroupAddon: React.FC<InputGroupAddonProps> = ({
  children,
  align = "inline-end",
  className,
}) => {
  const { orientation } = React.useContext(InputGroupContext);
  return (
    <div
      className={getInputGroupAddonClasses(align, orientation, className)}
      role="group"
    >
      {children}
    </div>
  );
};

const InputGroupButton: React.FC<InputGroupButtonProps> = ({
  className,
  type = "icon",
  size: sizeProp,
  icon,
  ...props
}) => {
  const { size: groupSize } = React.useContext(InputGroupContext);
  const size = (sizeProp ?? groupSize) as InputSize;

  return (
    <Button
      type={type}
      size={size}
      htmlType="button"
      icon={icon}
      className={cn(getInputGroupButtonClasses(size, type), "shrink-0", className)}
      {...props}
    />
  );
};

const InputGroupText: React.FC<InputGroupTextProps> = ({
  children,
  className,
}) => (
  <Typography
    as="span"
    variant="body-xs"
    color="secondary"
    className={cn("flex items-center", className)}
  >
    {children}
  </Typography>
);

const InputGroupTextarea = React.forwardRef<
  HTMLTextAreaElement,
  InputGroupTextareaProps
>(({ className, ...props }, ref) => (
  <div className="relative flex-1 min-w-0">
    <Textarea ref={ref} className={cn("w-full resize-none", className)} {...props} />
  </div>
));
InputGroupTextarea.displayName = "InputGroupTextarea";

export {
  InputGroup,
  InputGroupInput,
  InputGroupAddon,
  InputGroupButton,
  InputGroupText,
  InputGroupTextarea,
};
